import { defineStore } from 'pinia';
import type { IStepsState } from '../types/store-states.types';
import type { IStep } from '../types/steps.types';
import StepsCollection from '../utils/steps-collection';
import { useHistoryStore } from './history.store';
import { useConfigStore } from './config.store';


export const useStepsStore = defineStore('au-steps', {
  state: () => ({
    steps: [],
    currentStep: null,
  } as IStepsState),

  getters: {
    getSteps: (state) => state.steps,
    getCurrentStep: (state) => state.currentStep,
  },
  
  actions: {
    protectedInitializationSteps(steps: IStep[]) {
      const stepsCollection = new StepsCollection(steps);
      this.setSteps(stepsCollection.all);
      if (!!steps?.length) {
        this.setCurrentStep(stepsCollection.firstView.name);
      }
    },

    setCurrentStep(stepName: IStep['name']): void {
      this.currentStep = stepName;
      this.upHistory();
    },

    setSteps(steps: IStep[]): void {
      this.steps = steps;
    },

    addStep(step: IStep): void {
      this.steps.push(step);
    },

    nextStep(): void {
      if (this.steps.length <= 1) return;
      
      const withoutSkipSteps = this.steps.filter((step) => !step.skip);
      const firstStepIndex = 0;
      const lastStepIndex = withoutSkipSteps.length - 1;

      const currentStepSomeWithoutSkip = withoutSkipSteps.some((step) => step.name === this.currentStep);

      if (currentStepSomeWithoutSkip) {
        const currentStepIndex = withoutSkipSteps.findIndex((step) => step.name === this.currentStep);
        
        if (currentStepIndex < lastStepIndex) {
          this.currentStep = withoutSkipSteps[currentStepIndex + 1].name;
        }
        if (currentStepIndex === lastStepIndex) {
          this.currentStep = withoutSkipSteps[firstStepIndex].name;
        }
      } else {
        this.currentStep = withoutSkipSteps[lastStepIndex].name;
      }

      this.upHistory();
    },

    prevStep(): void {
      if (this.steps.length <= 1) return;
      const configStore = useConfigStore();
      const historyStore = useHistoryStore();

      const hasUseHistory = configStore.getUseHistory;
      const lastHistoryItemId = historyStore.getLastItem?.id;
      const prevHistoryItemId = historyStore.getLastItem?.prev;

      const withoutSkipSteps = this.steps.filter((step) => !step.skip);
      const firstStepIndex = 0;
      const lastStepIndex = withoutSkipSteps.length - 1;

      const currentStepSomeWithoutSkip = withoutSkipSteps.some((step) => step.name === this.currentStep);

      const useStep = () => {
        if (currentStepSomeWithoutSkip) {
          const currentStepIndex = withoutSkipSteps.findIndex((step) => step.name === this.currentStep);

          if (currentStepIndex > firstStepIndex) {
            this.currentStep = withoutSkipSteps[currentStepIndex - 1].name;
          }
          if (currentStepIndex === firstStepIndex) {
            this.currentStep = withoutSkipSteps[lastStepIndex].name;
          }
        } else {
          this.currentStep = withoutSkipSteps[firstStepIndex].name;
        }
      };

      const useHistory = () => {
        if (!prevHistoryItemId || !lastHistoryItemId) {
          useStep();
          return;
        }
        const prevHistoryItem = historyStore.getFullHistory.get(prevHistoryItemId);
        const stepNameFromHistory = prevHistoryItem?.data.stepName;
        if (stepNameFromHistory) {
          this.currentStep = stepNameFromHistory;
          historyStore.removeItems([prevHistoryItemId, lastHistoryItemId]);
        } else {
          useStep();
        }
      }

      if (hasUseHistory) {
        useHistory();
      } else {
        useStep();
      }

      this.upHistory();
    },

    resetSteps(): void {
      this.steps = [];
      this.currentStep = null;
      this.upHistory();
    },

    upHistory() {
      const historyStore = useHistoryStore();
      if (this.currentStep) {
        historyStore.addItem({
          stepName: this.currentStep
        });
      }
    },
  }
});